<!-- src/components/Stepper/FoodSurplusData.vue -->
<template>
  <div class="food-surplus-step">
    <StepperHeading :title="'Enter Food Surplus Data'" :step="2">
      <template #help>
        <p>
          In this step, you can define your food surplus by providing the following details:
        </p>
        <ul>
          <li>
            <strong>Surplus Name:</strong> Enter a name for the surplus (optional) 
            or pick from the dropdown if an existing product matches.
          </li>
          <li>
            <strong>Food Group(s):</strong> Select one or multiple relevant 
            food groups (optional).
          </li>
          <li>
            <strong>Food Subgroup(s):</strong> Specify subcategories if applicable (optional).
          </li>
        </ul>
        <p><strong>Important Notes:</strong></p>
        <ul>
          <li>
            All fields are optionalâ€"if no details are given, solutions will rely on your
            company info (NACE code, etc.).
          </li>
          <li>
            The more details you provide, the better the chances of finding a suitable solution.
          </li>
          <li>
            You can select from the dropdown or manually enter anything not listed.
          </li>
          <li>
            If an existing Surplus is chosen, its related Food Group(s) and Subgroup(s)
            will pre-fill but remain editable.
          </li>
          <li>
            While you can freely define your own surplus name, only products from the dropdown list 
            have detailed environmental data (LCI) available. If you choose to enter a custom name, 
            any environmental assessment report generated later may be less detailed.
          </li>
        </ul>
      </template>
    </StepperHeading>

    <!-- Food Surplus Form -->
    <form class="surplus-form">
      <!-- Product Information Section -->
      <div class="form-section" :class="{ 'dropdown-open': isAnyDropdownOpen }">
        <h3 class="section-title">Surplus Product Information</h3>
        
        <!-- Surplus Name -->
        <div class="form-field" ref="surplusBlock">
          <label for="surplusSearch">Surplus Name</label>
          <div class="input-wrapper position-relative">
            <i class="bi bi-basket input-icon"></i>
            <input
              type="text"
              id="surplusSearch"
              v-model="typedSurplusName"
              placeholder="Search or add Surplus (Product name)..."
              @focus="openSurplusDropdown"
              @input="handleSurplusTyping"
              @keydown.enter.prevent="confirmSurplusName"
              @blur="handleSurplusBlur"
              autocomplete="off"
              ref="surplusInput"
            />
            
            <button
              class="action-btn"
              :class="{ 'active': typedSurplusName.trim() }"
              type="button"
              @click="confirmSurplusName"
              :disabled="!typedSurplusName.trim()"
              title="Confirm Surplus Name"
            >
              <i class="bi bi-check-lg"></i>
            </button>
          
            <!-- Dropdown -->
            <div
              class="dropdown-menu"
              :class="{ 'show': isSurplusDropdownOpen && surplusDropdownItems.length }"
              v-if="isSurplusDropdownOpen && surplusDropdownItems.length"
              ref="surplusDropdown"
            >
              <div class="dropdown-items">
                <button
                  v-for="(prod, index) in surplusDropdownItems"
                  :key="prod._id || index"
                  class="dropdown-item"
                  @click.prevent="selectSurplus(prod)"
                >
                  {{ prod.product_name }}
                  <span v-if="prod.has_lci_data" class="lci-badge available">
                    LCI available
                  </span>
                  <span v-else class="lci-badge unavailable">
                    No LCI
                  </span>
                </button>
              </div>
            </div>
          </div>
          
          <!-- Selected Surplus -->
          <div v-if="wasteInfo.surplusName" class="selected-surplus">
            <strong>Selected Surplus:</strong> {{ wasteInfo.surplusName }}
            <button 
              type="button" 
              class="remove-btn" 
              @click="clearSelectedSurplus"
              aria-label="Clear selected surplus"
            >
              <i class="bi bi-x"></i>
            </button>
          </div>
        </div>
        
        <!-- Food Categories -->
        <div class="category-fields">
          <div class="form-field" ref="subgroupBlock">
            <label for="foodSubgroupSearch">Food Subgroup(s)</label>
            <div class="input-wrapper position-relative">
              <i class="bi bi-tags input-icon"></i>
              <input
                type="text"
                id="foodSubgroupSearch"
                v-model="foodSubgroupSearch"
                placeholder="Search or add Food Subgroups..."
                @focus="isFoodSubgroupDropdownOpen = true"
                @input="filterFoodSubgroupSuggestions"
                @keydown.enter.prevent="addFoodSubgroupFromSearch"
                @blur="handleSubgroupBlur"
                autocomplete="off"
                ref="subgroupInput"
              />
              
              <button
                class="action-btn"
                :class="{ 'active': foodSubgroupSearch.trim() }"
                type="button"
                @click="addFoodSubgroupFromSearch"
                :disabled="!foodSubgroupSearch.trim()"
                title="Add Food Subgroup"
              >
                <i class="bi bi-plus-lg"></i>
              </button>
            
              <!-- Dropdown -->
              <div
                class="dropdown-menu"
                :class="{ 'show': isFoodSubgroupDropdownOpen && filteredFoodSubgroupSuggestions.length }"
                v-if="isFoodSubgroupDropdownOpen && filteredFoodSubgroupSuggestions.length"
                ref="subgroupDropdown"
              >
                <div class="dropdown-items">
                  <button
                    v-for="(sub, i) in filteredFoodSubgroupSuggestions"
                    :key="sub + i"
                    class="dropdown-item"
                    @click.prevent="addFoodSubgroup(sub)"
                  >
                    {{ sub }}
                  </button>
                </div>
              </div>
            </div>
            
            <!-- Selected Subgroups -->
            <div class="selected-items">
              <div
                v-for="sub in wasteInfo.foodSubgroups"
                :key="sub"
                class="selected-item"
              >
                <span class="item-text">{{ sub }}</span>
                <button 
                  type="button" 
                  class="remove-btn" 
                  @click="removeFoodSubgroup(sub)"
                  aria-label="Remove food subgroup"
                >
                  <i class="bi bi-x"></i>
                </button>
              </div>
            </div>
          </div>

          <div class="form-field" ref="groupBlock">
            <label for="foodGroupSearch">Food Group(s)</label>
            <div class="input-wrapper position-relative">
              <i class="bi bi-tag input-icon"></i>
              <input
                type="text"
                id="foodGroupSearch"
                v-model="foodGroupSearch"
                placeholder="Search or add Food Groups..."
                @focus="isFoodGroupDropdownOpen = true"
                @input="filterFoodGroupSuggestions"
                @keydown.enter.prevent="addFoodGroupFromSearch"
                @blur="handleGroupBlur"
                autocomplete="off"
                ref="groupInput"
              />
              
              <button
                class="action-btn"
                :class="{ 'active': foodGroupSearch.trim() }"
                type="button"
                @click="addFoodGroupFromSearch"
                :disabled="!foodGroupSearch.trim()"
                title="Add Food Group"
              >
                <i class="bi bi-plus-lg"></i>
              </button>
            
              <!-- Dropdown -->
              <div
                class="dropdown-menu"
                :class="{ 'show': isFoodGroupDropdownOpen && filteredFoodGroupSuggestions.length }"
                v-if="isFoodGroupDropdownOpen && filteredFoodGroupSuggestions.length"
                ref="groupDropdown"
              >
                <div class="dropdown-items">
                  <button
                    v-for="(grp, idx) in filteredFoodGroupSuggestions"
                    :key="grp + idx"
                    class="dropdown-item"
                    @click.prevent="addFoodGroup(grp)"
                  >
                    {{ grp }}
                  </button>
                </div>
              </div>
            </div>
            
            <!-- Selected Groups -->
            <div class="selected-items">
              <div
                v-for="group in wasteInfo.foodGroups"
                :key="group"
                class="selected-item"
              >
                <span class="item-text">{{ group }}</span>
                <button 
                  type="button" 
                  class="remove-btn" 
                  @click="removeFoodGroup(group)"
                  aria-label="Remove food group"
                >
                  <i class="bi bi-x"></i>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      <!-- Combined Quantity & Seasonal Information Section -->
      <div class="form-section">
        <h3 class="section-title">Quantity Information</h3>
        
        <div class="quantity-fields">
          <!-- Amount -->
          <div class="form-field">
            <label for="amountValue">Amount</label>
            <div class="input-wrapper">
              <i class="bi bi-scale input-icon"></i>
              <input
                type="number"
                id="amountValue"
                v-model.number="wasteInfo.amount.value"
                placeholder="Enter amount"
                min="0"
              />
              <span class="unit-label">Kilograms</span>
            </div>
          </div>
          
          <!-- Frequency -->
          <div class="form-field">
            <label for="frequencyTimes">Frequency</label>
            <div class="frequency-inputs">
              <div class="input-wrapper frequency-times">
                <i class="bi bi-arrow-repeat input-icon"></i>
                <input
                  type="number"
                  id="frequencyTimes"
                  v-model.number="wasteInfo.frequency.times"
                  placeholder="Times"
                  min="1"
                />
              </div>
              <span class="frequency-text">times per</span>
              <select 
                class="frequency-interval" 
                v-model="wasteInfo.frequency.interval"
              >
                <option value="once">Once</option>
                <option value="week">Week</option>
                <option value="month">Month</option>
              </select>
            </div>
          </div>
        </div>
        
        <!-- Seasonal Information Toggle -->
        <div class="seasonal-toggle-container">
          <div class="seasonal-toggle">
            <label for="seasonalCheckbox" class="switch">
              <input type="checkbox" id="seasonalCheckbox" v-model="wasteInfo.seasonal" />
              <span class="slider"></span>
            </label>
            <span class="toggle-label">Seasonal Availability</span>
          </div>
        </div>
        
        <!-- Seasonal Content (appears when toggle is on) -->
        <div v-if="wasteInfo.seasonal" class="seasonal-content">
          <div class="info-note">
            <i class="bi bi-info-circle"></i>
            <p>
              If your Surplus is seasonal, you can specify your monthly capacity here.
              This is a percentage for each month (100 = entire monthly amount).
            </p>
          </div>
          
          <div class="monthly-capacities">
            <div
              v-for="(month, index) in wasteInfo.monthlyCapacities"
              :key="index"
              class="month-item"
            >
              <div class="month-header">
                <div class="month-checkbox">
                  <input type="checkbox" :id="'month-' + index" v-model="month.active" />
                  <label :for="'month-' + index">{{ month.name }}</label>
                </div>
              </div>
              
              <div v-if="month.active" class="month-capacity">
                <div class="input-wrapper">
                  <i class="bi bi-percent input-icon"></i>
                  <input
                    type="number"
                    v-model.number="month.capacity"
                    min="0"
                    max="999"
                    placeholder="Capacity"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Additional Information Section -->
      <div class="form-section">
        <h3 class="section-title">Additional Information</h3>
        
        <div class="form-field">
          <label for="additionalInfo">Additional Surplus Information</label>
          <div class="input-wrapper textarea-wrapper">
            <i class="bi bi-file-text input-icon"></i>
            <textarea
              id="additionalInfo"
              v-model="wasteInfo.additionalSurplusInfo"
              placeholder="Provide details about the food condition (e.g., frozen, fresh, chopped)or additional information about availability that could help find suitable solutions."
              rows="4"
            ></textarea>
          </div>
        </div>
      </div>
    </form>
  </div>
</template>

<script>
import StepperHeading from './StepperHeading.vue';
import { useStepperStore } from '@/stores/stepper';
import { usePageContextStore } from '@/stores/pageContext';
import axios from 'axios';

// Simple helper to remove duplicates by product_name
function uniqueProductsByName(products) {
  const seen = new Set();
  return products.filter((item) => {
    if (!item.product_name) return false;
    const lowerName = item.product_name.toLowerCase();
    if (seen.has(lowerName)) {
      return false;
    }
    seen.add(lowerName);
    return true;
  });
}

// Debounce utility
function debounce(fn, delay = 150) {
  let timeout;
  return (...args) => {
    clearTimeout(timeout);
    timeout = setTimeout(() => {
      fn(...args);
    }, delay);
  };
}

export default {
  name: 'FoodSurplusData',
  components: { StepperHeading },

  setup() {
    const stepperStore = useStepperStore();
    const pageContextStore = usePageContextStore();

    return {
      wasteInfo: stepperStore.wasteInfo,
      stepperStore,
      pageContextStore,
    };
  },

  data() {
    return {
      typedSurplusName: '',
      products: [],
      foodGroupSearch: '',
      foodSubgroupSearch: '',
      isFoodGroupDropdownOpen: false,
      isFoodSubgroupDropdownOpen: false,
      isSurplusDropdownOpen: false,
      primaryColor: '#87b140',
      
      // Flag to prevent handling blur events when clicking dropdowns
      isProcessingDropdownClick: false,

      // Debounced function reference
      handleSurplusTyping: null,
    };
  },

  async mounted() {
    try {
      const response = await axios.get('/products');
      // Remove any duplicates in the fetched array
      const unique = uniqueProductsByName(response.data || []);
      this.products = unique;
    } catch (error) {
      console.error('Error loading products:', error);
    }

    if (this.wasteInfo.surplusName) {
      this.typedSurplusName = this.wasteInfo.surplusName;
    }

    this.pageContextStore.setPageContext(
      'Enter Food Surplus Data',
      'Here, the user must define their food surplus for which they are seeking solutions. They can select or define multiple food groups/subgroups and specify an additional surplus. If an LCI (Life Cycle Inventory) is available for a surplus, it means that environmental impact data is known and can later be used for the LCA. If no surplus is selected, the chosen food groups/subgroups will serve as the basis for solution finding.',
      'We allow selection of multiple groups, etc.'
    );

    // Short delay when typing in Surplus Name
    this.handleSurplusTyping = debounce(this.openSurplusDropdown, 120);

    document.addEventListener('click', this.handleClickOutside);
  },

  beforeUnmount() {
    document.removeEventListener('click', this.handleClickOutside);
  },

  computed: {
    // We only build the array once (deduplicate in mounted).
    // Then we filter here. This array is used by the Surplus dropdown.
    surplusDropdownItems() {
      // If typed field is empty, we might show nothing or everything
      const typed = this.typedSurplusName.trim().toLowerCase();
      if (!typed) return [];
      return this.products.filter((prod) =>
        prod.product_name.toLowerCase().includes(typed)
      );
    },

    filteredFoodGroupSuggestions() {
      // Some real-time filtering for "foodGroups"
      const setAll = new Set();
      
      // Add Food Waste as a default option
      setAll.add("Food Waste");
      
      this.products.forEach((p) => {
        if (p.food_group && Array.isArray(p.food_group)) {
          p.food_group.forEach((g) => setAll.add(g));
        }
      });
      // remove already chosen from the set
      this.wasteInfo.foodGroups.forEach((chosen) => {
        if (setAll.has(chosen)) setAll.delete(chosen);
      });
      // convert to array & filter
      let arr = [...setAll];
      if (this.foodGroupSearch.trim()) {
        const q = this.foodGroupSearch.trim().toLowerCase();
        arr = arr.filter((g) => g.toLowerCase().includes(q));
      }
      return arr.sort();
    },

    filteredFoodSubgroupSuggestions() {
      // Similar approach for subgroups
      const setAll = new Set();
      
      // Add some common food waste subcategories
      const foodWasteSubgroups = [
        "Mixed Food Waste",
        "Expired Food",
        "Production Scraps",
        "Damaged Products",
        "Preparation Residues"
      ];
      
      // Only add these subgroups if Food Waste is selected as a food group
      if (this.wasteInfo.foodGroups.includes("Food Waste")) {
        foodWasteSubgroups.forEach(sg => setAll.add(sg));
      }
      
      this.products.forEach((p) => {
        if (p.food_subgroup && Array.isArray(p.food_subgroup)) {
          p.food_subgroup.forEach((sg) => setAll.add(sg));
        }
      });
      // remove chosen
      this.wasteInfo.foodSubgroups.forEach((chosen) => {
        if (setAll.has(chosen)) setAll.delete(chosen);
      });
      let arr = [...setAll];
      if (this.foodSubgroupSearch.trim()) {
        const q = this.foodSubgroupSearch.trim().toLowerCase();
        arr = arr.filter((sg) => sg.toLowerCase().includes(q));
      }
      return arr.sort();
    },

    // Neu: Ermittelt, ob eines der Dropdowns geÃ¶ffnet ist.
    isAnyDropdownOpen() {
      return this.isSurplusDropdownOpen || this.isFoodGroupDropdownOpen || this.isFoodSubgroupDropdownOpen;
    },
  },

  methods: {
    openSurplusDropdown() {
      this.isSurplusDropdownOpen = true;
    },

    // Handle blur events for each input field
    handleSurplusBlur() {
      // Use setTimeout to ensure this happens after potential dropdown clicks
      setTimeout(() => {
        if (!this.isProcessingDropdownClick && this.typedSurplusName.trim()) {
          this.confirmSurplusName();
        }
      }, 200);
    },

    handleSubgroupBlur() {
      setTimeout(() => {
        if (!this.isProcessingDropdownClick && this.foodSubgroupSearch.trim()) {
          this.addFoodSubgroupFromSearch();
        }
      }, 200);
    },

    handleGroupBlur() {
      setTimeout(() => {
        if (!this.isProcessingDropdownClick && this.foodGroupSearch.trim()) {
          this.addFoodGroupFromSearch();
        }
      }, 200);
    },

    confirmSurplusName() {
      const text = this.typedSurplusName.trim();
      if (!text) {
        this.isSurplusDropdownOpen = false;
        return;
      }
      // Look for an exact match
      const exact = this.surplusDropdownItems.find(
        (p) => p.product_name.toLowerCase() === text.toLowerCase()
      );
      if (exact) {
        this.selectSurplus(exact);
      } else {
        // no exact => user typed something new
        this.wasteInfo.surplusName = text;
      }
      this.isSurplusDropdownOpen = false;
    },

    selectSurplus(item) {
      this.isProcessingDropdownClick = true;
      this.wasteInfo.surplusName = item.product_name;
      this.typedSurplusName = item.product_name;
      // fill in groups if not already present
      if (Array.isArray(item.food_group)) {
        item.food_group.forEach((fg) => {
          if (!this.wasteInfo.foodGroups.includes(fg)) {
            this.wasteInfo.foodGroups.push(fg);
          }
        });
      }
      if (Array.isArray(item.food_subgroup)) {
        item.food_subgroup.forEach((sg) => {
          if (!this.wasteInfo.foodSubgroups.includes(sg)) {
            this.wasteInfo.foodSubgroups.push(sg);
          }
        });
      }
      this.isSurplusDropdownOpen = false;
      
      // Reset the flag after a short delay
      setTimeout(() => {
        this.isProcessingDropdownClick = false;
      }, 300);
    },

    clearSelectedSurplus() {
      this.wasteInfo.surplusName = '';
      this.typedSurplusName = '';
    },

    // FoodGroup
    filterFoodGroupSuggestions() {
      this.isFoodGroupDropdownOpen = true;
    },
    addFoodGroupFromSearch() {
      const val = this.foodGroupSearch.trim();
      if (val && !this.wasteInfo.foodGroups.includes(val)) {
        this.wasteInfo.foodGroups.push(val);
      }
      this.foodGroupSearch = '';
      this.isFoodGroupDropdownOpen = false;
    },
    addFoodGroup(val) {
      this.isProcessingDropdownClick = true;
      if (!this.wasteInfo.foodGroups.includes(val)) {
        this.wasteInfo.foodGroups.push(val);
      }
      this.foodGroupSearch = '';
      this.isFoodGroupDropdownOpen = false;
      
      // Reset the flag after a short delay
      setTimeout(() => {
        this.isProcessingDropdownClick = false;
      }, 300);
    },
    removeFoodGroup(val) {
      this.wasteInfo.foodGroups = this.wasteInfo.foodGroups.filter((g) => g !== val);
    },

    // FoodSubgroup
    filterFoodSubgroupSuggestions() {
      this.isFoodSubgroupDropdownOpen = true;
    },
    addFoodSubgroupFromSearch() {
      const val = this.foodSubgroupSearch.trim();
      if (val && !this.wasteInfo.foodSubgroups.includes(val)) {
        this.wasteInfo.foodSubgroups.push(val);
      }
      this.foodSubgroupSearch = '';
      this.isFoodSubgroupDropdownOpen = false;
    },
    addFoodSubgroup(val) {
      this.isProcessingDropdownClick = true;
      if (!this.wasteInfo.foodSubgroups.includes(val)) {
        this.wasteInfo.foodSubgroups.push(val);
      }
      this.foodSubgroupSearch = '';
      this.isFoodSubgroupDropdownOpen = false;
      
      // Reset the flag after a short delay
      setTimeout(() => {
        this.isProcessingDropdownClick = false;
      }, 300);
    },
    removeFoodSubgroup(val) {
      this.wasteInfo.foodSubgroups = this.wasteInfo.foodSubgroups.filter((s) => s !== val);
    },

    // Click outside => close dropdown
    handleClickOutside(e) {
      // Surplus
      if (
        this.$refs.surplusBlock &&
        (this.$refs.surplusBlock.contains(e.target) ||
         (this.$refs.surplusDropdown && this.$refs.surplusDropdown.contains(e.target)))
      ) {
        // do nothing
      } else {
        this.isSurplusDropdownOpen = false;
      }

      // Food Group
      if (
        this.$refs.groupBlock &&
        (this.$refs.groupBlock.contains(e.target) ||
         (this.$refs.groupDropdown && this.$refs.groupDropdown.contains(e.target)))
      ) {
        // do nothing
      } else {
        this.isFoodGroupDropdownOpen = false;
      }

      // Food Subgroup
      if (
        this.$refs.subgroupBlock &&
        (this.$refs.subgroupBlock.contains(e.target) ||
         (this.$refs.subgroupDropdown && this.$refs.subgroupDropdown.contains(e.target)))
      ) {
        // do nothing
      } else {
        this.isFoodSubgroupDropdownOpen = false;
      }
    },
  },
};
</script>

<style scoped>
.food-surplus-step {
  width: 100%;
}

.surplus-form {
  display: flex;
  flex-direction: column;
  gap: 30px;
}

/* Neues Form Section Design */
.form-section {
  background-color: #ffffff;
  border-radius: 12px;
  padding: 24px;
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.05), 0 5px 10px rgba(0, 0, 0, 0.02);
  border: 1px solid rgba(135, 177, 64, 0.1);
  transition: all 0.3s ease;
  position: relative;
  overflow: visible;
}

/* Nur anwenden, wenn kein Dropdown offen ist */
.form-section:not(.dropdown-open):hover {
  box-shadow: 0 12px 28px rgba(0, 0, 0, 0.07), 0 6px 12px rgba(0, 0, 0, 0.03);
  transform: translateY(-2px);
}

.form-section::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 4px;
  background-color: #87B140;
}

.section-title {
  font-size: 1.25rem;
  font-weight: 700;
  color: #333;
  margin-bottom: 24px;
  padding-bottom: 10px;
  border-bottom: 1px solid #eee;
  position: relative;
}

.section-title::after {
  content: '';
  position: absolute;
  bottom: -1px;
  left: 0;
  width: 60px;
  height: 3px;
  background-color: #87B140;
  border-radius: 3px;
}

.form-field {
  margin-bottom: 20px;
}

.form-field:last-child {
  margin-bottom: 0;
}

label {
  display: block;
  margin-bottom: 6px;
  font-weight: 500;
  color: #555;
  font-size: 0.95rem;
}

.input-wrapper {
  position: relative;
  display: flex;
  align-items: center;
  width: 100%;
}

.input-icon {
  position: absolute;
  left: 10px;
  color: #87b140;
  font-size: 1.1rem;
  pointer-events: none;
}

input, textarea, select {
  width: 100%;
  padding: 12px 12px 12px 40px;
  border: 1px solid #ddd;
  border-radius: 6px;
  font-size: 1rem;
  transition: all 0.3s ease;
}

input:focus, textarea:focus, select:focus {
  outline: none;
  border-color: #87b140;
  box-shadow: 0 0 0 2px rgba(135, 177, 64, 0.2);
}

textarea {
  resize: vertical;
  min-height: 100px;
}

.action-btn {
  position: absolute;
  right: 5px;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: #f0f0f0;
  border: none;
  color: #555;
  font-size: 1rem;
  cursor: pointer;
  transition: all 0.2s ease;
}

.action-btn:hover {
  background-color: #e0e0e0;
}

.action-btn.active {
  background-color: #87b140;
  color: white;
}

.action-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

/* Field hint styling */
.field-hint {
  margin-top: 6px;
  font-size: 0.85rem;
  color: #666;
  display: flex;
  align-items: center;
  gap: 6px;
}

.field-hint i {
  color: #87B140;
}

/* Dropdown styling */
.dropdown-menu {
  background: white;
  border-radius: 6px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  margin-top: 5px;
  max-height: 250px;
  overflow-y: auto;
  z-index: 9999;
  border: 1px solid #eee;
  display: none;
  position: absolute;
  top: 100%;
  left: 0;
  width: 100%;
}

.dropdown-menu.show {
  display: block;
}

.dropdown-items {
  padding: 5px 0;
}

.dropdown-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  text-align: left;
  padding: 10px 15px;
  background: none;
  border: none;
  cursor: pointer;
  font-size: 0.95rem;
  color: #333;
  transition: background-color 0.2s;
}

.dropdown-item:hover {
  background-color: rgba(135, 177, 64, 0.1);
}

.lci-badge {
  font-size: 0.8rem;
  padding: 2px 8px;
  border-radius: 12px;
}

.lci-badge.available {
  background-color: rgba(135, 177, 64, 0.1);
  color: #87b140;
}

.lci-badge.unavailable {
  background-color: rgba(220, 53, 69, 0.1);
  color: #dc3545;
}

/* Selected items styling */
.selected-surplus {
  margin-top: 10px;
  padding: 8px 12px;
  background-color: #f8f9fa;
  border-radius: 6px;
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.selected-items {
  margin-top: 15px;
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
}

.selected-item {
  display: flex;
  align-items: center;
  background: rgba(135, 177, 64, 0.1);
  border: 1px solid rgba(135, 177, 64, 0.3);
  border-radius: 20px;
  padding: 6px 10px;
  font-size: 0.9rem;
}

.item-text {
  color: #333;
  max-width: 200px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.remove-btn {
  background: none;
  border: none;
  color: #666;
  margin-left: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  width: 20px;
  height: 20px;
  border-radius: 50%;
  padding: 0;
  font-size: 1rem;
  transition: all 0.2s;
}

.remove-btn:hover {
  background-color: rgba(0, 0, 0, 0.1);
  color: #e74c3c;
}

/* Category fields */
.category-fields {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

/* Hier den Abstand der Felder in der KategorieÃ¼bersicht anpassen */
.category-fields .form-field {
  margin-bottom: 0;
}

/* Quantity fields */
.quantity-fields {
  display: flex;
  flex-wrap: wrap;
  gap: 20px;
  margin-bottom: 20px;
}

.quantity-fields .form-field {
  flex: 1;
  min-width: 200px;
}

.unit-label {
  position: absolute;
  right: 10px;
  color: #6c757d;
  font-size: 0.9rem;
}

.frequency-inputs {
  display: flex;
  align-items: center;
  gap: 10px;
}

.frequency-times {
  width: 120px;
}

.frequency-text {
  white-space: nowrap;
  color: #6c757d;
}

.frequency-interval {
  flex: 1;
  min-width: 100px;
  padding: 12px;
  border-radius: 6px;
  border: 1px solid #ddd;
  color: #333;
  background-color: white;
  font-size: 1rem;
}

.frequency-interval:focus {
  outline: none;
  border-color: #87b140;
  box-shadow: 0 0 0 2px rgba(135, 177, 64, 0.2);
}

/* Seasonal toggle container (added for combined section) */
.seasonal-toggle-container {
  padding-top: 10px;
  margin-top: 10px;
  border-top: 1px dashed #e0e0e0;
}

/* Seasonal toggle */
.seasonal-toggle {
  display: flex;
  align-items: center;
}

.toggle-label {
  margin-left: 10px;
  font-weight: 500;
}

/* Toggle switch */
.switch {
  position: relative;
  display: inline-block;
  width: 50px;
  height: 24px;
  margin: 0;
}

.switch input {
  opacity: 0;
  width: 0;
  height: 0;
}

.slider {
  position: absolute;
  cursor: pointer;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: #ccc;
  transition: .4s;
  border-radius: 24px;
}

.slider:before {
  position: absolute;
  content: "";
  height: 16px;
  width: 16px;
  left: 4px;
  bottom: 4px;
  background-color: white;
  transition: .4s;
  border-radius: 50%;
}

input:checked + .slider {
  background-color: #87b140;
}

input:focus + .slider {
  box-shadow: 0 0 1px #87b140;
}

input:checked + .slider:before {
  transform: translateX(26px);
}

/* Seasonal content */
.seasonal-content {
  margin-top: 20px;
  animation: fadeIn 0.3s ease-out;
}

@keyframes fadeIn {
  from { opacity: 0; }
  to { opacity: 1; }
}

.info-note {
  background-color: #f8f9fa;
  border-left: 3px solid #87b140;
  padding: 12px 15px;
  margin-bottom: 20px;
  border-radius: 6px;
  display: flex;
  align-items: flex-start;
}

.info-note i {
  color: #87b140;
  font-size: 1.1rem;
  margin-right: 10px;
  margin-top: 2px;
}

.info-note p {
  margin: 0;
  color: #555;
}

/* Monthly capacities */
.monthly-capacities {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(160px, 1fr));
  gap: 15px;
}

.month-item {
  background-color: #f8f9fa;
  border-radius: 6px;
  padding: 10px;
}

.month-header {
  margin-bottom: 8px;
}

.month-checkbox {
  display: flex;
  align-items: center;
}

.month-checkbox input[type="checkbox"] {
  margin-right: 8px;
  width: 16px;
  height: 16px;
}

.month-checkbox label {
  margin-bottom: 0;
  display: inline;
}

.month-capacity .input-wrapper {
  margin-top: 5px;
}

.month-capacity input {
  padding-left: 30px;
  padding-right: 10px;
}

/* Additional styling for info-note in a grid */
.mt-3 {
  margin-top: 1rem;
}

/* Responsive adjustments */
@media (max-width: 768px) {
  .form-section {
    padding: 20px;
  }
  
  .quantity-fields {
    flex-direction: column;
    gap: 15px;
  }
  
  .monthly-capacities {
    grid-template-columns: repeat(auto-fill, minmax(140px, 1fr));
  }
  
  .section-title {
    font-size: 1.15rem;
    margin-bottom: 18px;
  }
  
  input, textarea, select {
    font-size: 0.95rem;
  }
}

@media (max-width: 576px) {
  .form-section {
    padding: 16px;
    border-radius: 10px;
  }
  
  .monthly-capacities {
    grid-template-columns: repeat(auto-fill, minmax(120px, 1fr));
  }
  
  .frequency-inputs {
    flex-wrap: wrap;
  }
  
  .frequency-times {
    width: 100%;
  }

  .section-title {
    font-size: 1.05rem;
    margin-bottom: 16px;
  }
  
  label {
    font-size: 0.9rem;
  }
  
  .selected-item {
    font-size: 0.85rem;
    padding: 4px 8px;
  }
  
  .item-text {
    max-width: 150px;
  }
}
</style>